import { SlickSlider } from './classes/class_slick.js';
import { SmoothScroll } from './classes/SmoothScroll.js';

document.addEventListener('DOMContentLoaded', () => {
  /* =========================================================
       共通ユーティリティ
    ========================================================= */
  const breakPoint = '960px';
  const mediaQuery = window.matchMedia(`(max-width:${breakPoint})`);
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const FOCUSABLE = [
    'a[href]', 'area[href]', 'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])', 'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function getFocusableWithin(root) {
    return Array.from(root.querySelectorAll(FOCUSABLE))
      .filter(el => el.offsetParent !== null || el === document.activeElement);
  }

  function trapFocus(e, container) {
    if (e.key !== 'Tab') return;
    const focusables = getFocusableWithin(container);
    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  }

  /* =========================================================
     スクロールロック（iOS対応）
  ========================================================= */
  let scrollY = 0;
  function lockScroll() {
    scrollY = window.scrollY || window.pageYOffset || 0;
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.classList.add('is-nav-locked');
  }
  function unlockScroll() {
    document.body.classList.remove('is-nav-locked');
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    window.scrollTo(0, scrollY);
  }

  /* =========================================================
     共通レイヤー制御関数（ナビ／モーダル兼用）
  ========================================================= */
  function makeLayerController({ root, overlay, openBtns = [], closeBtns = [], toggleBtns = [] }) {
    if (!root) return;

    let lastFocused = null;
    const ACTIVE_CLASS = 'is-active';

    function isOpen() {
      return root.classList.contains(ACTIVE_CLASS);
    }

    function syncToggleState(opened) {
      toggleBtns.forEach(btn => {
        btn.classList.toggle(ACTIVE_CLASS, opened);
        btn.setAttribute('aria-expanded', opened ? 'true' : 'false');
        const sr = btn.querySelector('.sr-only');
        if (sr) sr.textContent = opened ? '閉じる' : '開く';
      });
    }

    function onKeydown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
      }
      trapFocus(e, root);
    }

    function open() {
      if (isOpen()) return;
      lastFocused = document.activeElement;

      if (overlay) overlay.classList.add(ACTIVE_CLASS);
      root.classList.add(ACTIVE_CLASS);

      syncToggleState(true);
      lockScroll();

      const focusables = getFocusableWithin(root);
      (focusables[0] || root).focus();

      document.addEventListener('keydown', onKeydown);
      if (overlay) overlay.addEventListener('click', close, { once: true });
    }

    function close() {
      if (!isOpen()) return;

      if (overlay) overlay.classList.remove(ACTIVE_CLASS);
      root.classList.remove(ACTIVE_CLASS);

      syncToggleState(false);
      unlockScroll();

      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus();
      }

      document.removeEventListener('keydown', onKeydown);
    }

    function toggle() {
      if (isOpen()) close();
      else open();
    }

    const anchorLinks = Array.from(root.querySelectorAll('a[href^="#"]'));

    anchorLinks.forEach((link) => {
      link.addEventListener('click', (e) => {
        const hash = link.getAttribute('href');

        if (!hash || hash === '#') {
          close();
          return;
        }

        const target = document.querySelector(hash);

        if (!target) {
          close();
          return;
        }

        e.preventDefault();

        close();

        requestAnimationFrame(() => {
          const targetPosition = smoothScroll.calculateTargetPosition(target);
          smoothScroll.scroll(targetPosition);
        });
      });
    });

    openBtns.forEach(btn => btn.addEventListener('click', open));
    closeBtns.forEach(btn => btn.addEventListener('click', close));
    toggleBtns.forEach(btn => btn.addEventListener('click', toggle));

    syncToggleState(isOpen());

    return { open, close, toggle };
  }

  const gnavRoot = document.querySelector('.global-nav');
  const overlay = document.querySelector('.nav-overlay');
  const navBtn = document.querySelector('.nav-button');

  if (gnavRoot && overlay && navBtn) {
    makeLayerController({
      root: gnavRoot,
      overlay: overlay,
      toggleBtns: [navBtn],
    })
  }

  const recruitMenu = document.querySelector('.recruit-nav');
  const recruitBtn = document.querySelector('.header-recruit__button');
  const recruitCloseBtn = document.querySelector('.recruit-nav__close-button');

  if (recruitMenu && recruitBtn && recruitCloseBtn) {
    makeLayerController({
      root: recruitMenu,
      toggleBtns: [recruitBtn],
      closeBtns: [recruitCloseBtn],
    });
  }

  /* =========================================================
     ドロップダウンメニュー
  ========================================================= */
  function createDropdownGroup(selectors = {}) {
    const {
      itemSelector = '.header__menu-item',
      buttonSelector = '.header__menu-button',
      panelSelector = '.header__menu-child',
      activeClass = 'is-active',
    } = selectors;

    const items = Array.from(document.querySelectorAll(itemSelector));

    if (!items.length) return;

    const controllers = [];

    function closeOthers(currentController) {
      controllers.forEach(controller => {
        if (controller !== currentController) {
          controller.close();
        }
      });
    }

    function createController(item) {
      const button = item.querySelector(buttonSelector);
      const panel = item.querySelector(panelSelector);

      if (!button || !panel) return null;

      let lastFocused = null;

      function isOpen() {
        return button.classList.contains(activeClass) && panel.classList.contains(activeClass);
      }

      function syncState(opened) {
        button.classList.toggle(activeClass, opened);
        panel.classList.toggle(activeClass, opened);
        button.setAttribute('aria-expanded', opened ? 'true' : 'false');

        const sr = button.querySelector('.sr-only');
        if (sr) {
          sr.textContent = opened ? '閉じる' : '開く';
        }
      }

      function open() {
        if (isOpen()) return;

        closeOthers(controller);
        lastFocused = document.activeElement;
        syncState(true);
      }

      function close() {
        if (!isOpen()) return;

        syncState(false);
      }

      function toggle() {
        if (isOpen()) {
          close();
        } else {
          open();
        }
      }

      function onButtonClick() {
        toggle();
      }

      function onDocumentClick(e) {
        if (!item.contains(e.target)) {
          close();
        }
      }

      function onDocumentKeydown(e) {
        if (e.key === 'Escape' && isOpen()) {
          e.preventDefault();
          close();

          if (lastFocused && typeof lastFocused.focus === 'function') {
            lastFocused.focus();
          }
        }
      }

      const anchorLinks = Array.from(panel.querySelectorAll('a[href^="#"]'));
      anchorLinks.forEach(link => {
        link.addEventListener('click', close);
      });

      button.setAttribute('aria-expanded', 'false');

      if (panel.id) {
        button.setAttribute('aria-controls', panel.id);
      }

      button.addEventListener('click', onButtonClick);
      document.addEventListener('click', onDocumentClick);
      document.addEventListener('keydown', onDocumentKeydown);

      syncState(false);

      const controller = { open, close, toggle };
      return controller;
    }

    items.forEach(item => {
      const controller = createController(item);
      if (controller) {
        controllers.push(controller);
      }
    });

    return {
      closeAll() {
        controllers.forEach(controller => controller.close());
      },
    };
  }

  createDropdownGroup({
    itemSelector: '.header__menu-item',
    buttonSelector: '.header__menu-button',
    panelSelector: '.header__menu-child',
    activeClass: 'is-active',
  });

  /* =========================================================
    smooth scroll
  ========================================================= */
  const smoothScroll = new SmoothScroll();

  /* =========================================================
    Accordion（data属性ベース・クラス非依存）
  ========================================================= */
  function initAccordion({
    root,
    item = '[data-accordion-item]',
    heading = '[data-accordion-heading]',
    panel = '[data-accordion-panel]',
    openClass = 'is-open',
    openClassTargets = [':item'],
    single = false,
  } = {}) {
    const roots = Array.from(document.querySelectorAll(root));
    if (!roots.length) return;

    const endHandlers = new WeakMap();

    const getOpenTargets = (itemEl, headingEl, panelEl) => {
      const targets = [headingEl, panelEl];
      if (openClassTargets.includes(':item')) targets.push(itemEl);
      return targets.filter(Boolean);
    };

    const animateHeight = (panelEl, isOpen) => {
      if (prefersReducedMotion) {
        panelEl.style.height = isOpen ? 'auto' : '0px';
        return;
      }

      const prev = endHandlers.get(panelEl);
      if (prev) panelEl.removeEventListener('transitionend', prev);

      const startH = panelEl.getBoundingClientRect().height;
      panelEl.style.height = `${startH}px`;
      panelEl.offsetHeight;

      if (isOpen) {
        panelEl.style.height = `${panelEl.scrollHeight}px`;

        const onEnd = (e) => {
          if (e.target !== panelEl || e.propertyName !== 'height') return;

          panelEl.style.height = 'auto';
          panelEl.removeEventListener('transitionend', onEnd);
          endHandlers.delete(panelEl);
        };

        endHandlers.set(panelEl, onEnd);
        panelEl.addEventListener('transitionend', onEnd);
      } else {
        requestAnimationFrame(() => {
          panelEl.style.height = '0px';
        });
      }
    };

    roots.forEach((rootEl) => {
      const items = Array.from(rootEl.querySelectorAll(item));

      const setState = (itemEl, isOpen, opts = {}) => {
        const headingEl = itemEl.querySelector(heading);
        const panelEl = itemEl.querySelector(panel);
        if (!headingEl || !panelEl) return;

        getOpenTargets(itemEl, headingEl, panelEl).forEach((el) => {
          el.classList.toggle(openClass, isOpen);
        });

        headingEl.setAttribute('aria-expanded', String(isOpen));
        panelEl.setAttribute('aria-hidden', String(!isOpen));

        const stateTextEl = itemEl.querySelector('[data-state-text]');

        if (stateTextEl) {
          stateTextEl.textContent = isOpen ? '閉じる' : '開く';
          stateTextEl.classList.toggle('is-open', isOpen);
        }

        if (opts.skipAnim) {
          panelEl.style.height = isOpen ? 'auto' : '0px';
        } else {
          animateHeight(panelEl, isOpen);
        }
      };

      const closeAllExcept = (current) => {
        items.forEach((it) => {
          if (it !== current) setState(it, false);
        });
      };

      items.forEach((itemEl) => {
        const headingEl = itemEl.querySelector(heading);
        const panelEl = itemEl.querySelector(panel);
        if (!headingEl || !panelEl) return;

        if (headingEl.tagName !== 'BUTTON') {
          headingEl.setAttribute('role', 'button');
          headingEl.tabIndex = 0;
        }

        const initiallyOpen =
          itemEl.classList.contains(openClass) ||
          headingEl.classList.contains(openClass) ||
          panelEl.classList.contains(openClass);

        setState(itemEl, initiallyOpen, { skipAnim: true });

        const toggle = () => {
          const willOpen = !headingEl.classList.contains(openClass);

          if (single && willOpen) {
            closeAllExcept(itemEl);
          }

          setState(itemEl, willOpen);
        };

        itemEl.addEventListener('click', (e) => {
          const interactiveEl = e.target.closest('a,button,input,select,textarea,label');

          if (interactiveEl && interactiveEl !== headingEl) return;
          if (panelEl.contains(e.target)) return;

          toggle();
        });

        headingEl.addEventListener('keydown', (e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;

          e.preventDefault();
          toggle();
        });
      });
    });
  }

  initAccordion({
    root: '[data-accordion]',
    item: '[data-accordion-item]',
    heading: '[data-accordion-heading]',
    panel: '[data-accordion-panel]',
    single: false,
    openClassTargets: [':item'],
  });

  /* ================================
    Accordion 全close（タブ切替用）
  ================================ */
  function closeAccordionsIn(container) {
    if (!container) return;
    container.querySelectorAll('[data-accordion-item]').forEach((item) => {
      const heading = item.querySelector('[data-accordion-heading]');
      const panel = item.querySelector('[data-accordion-panel]');
      if (!heading || !panel) return;

      item.classList.remove('is-open');
      heading.classList.remove('is-open');
      panel.classList.remove('is-open');

      heading.setAttribute('aria-expanded', 'false');
      panel.setAttribute('aria-hidden', 'true');
      panel.style.height = '0px';
    });
  }

  /* =========================================================
    タブ（data属性ベース・クラス非依存）
  ========================================================= */
  function initTabs({
    root = '[data-tabs]',
    list = '[data-tab-list]',
    item = '[data-tab-item][data-tab]',
    panelsRoot = '[data-tab-panels]',
    panel = '[data-tab-panel][data-panel]',
    currentClass = 'current',
    showClass = 'is-show',
  } = {}) {

    const roots = Array.from(document.querySelectorAll(root));

    roots.forEach((rootEl) => {

      const listEl = rootEl.querySelector(list);
      const panelsEl = rootEl.querySelector(panelsRoot) || document.querySelector(panelsRoot);

      if (!listEl || !panelsEl) return;

      const items = Array.from(listEl.querySelectorAll(item));
      const panels = Array.from(panelsEl.querySelectorAll(panel));

      listEl.setAttribute('role', 'tablist');

      items.forEach((tab, i) => {
        tab.setAttribute('role', 'tab');
        tab.setAttribute('tabindex', i === 0 ? '0' : '-1');
        tab.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      });

      panels.forEach((p) => {
        p.setAttribute('role', 'tabpanel');
      });

      const syncTabList = (activeTab) => {
        const allTab = items.find((t) => t.dataset.tab === 'all');

        if (allTab && activeTab.dataset.tab !== 'all') {
          const listInner = allTab.parentElement;

          if (listInner) {
            listInner.prepend(allTab);
          }
        }

        items.forEach((t) => {
          t.hidden = t === activeTab;
        });
      };

      const activateTab = (tab) => {

        const tabId = tab.dataset.tab;
        const currentLabel = rootEl.querySelector('[data-tab-current]');

        items.forEach((t) => {
          const selected = t === tab;

          t.classList.toggle(currentClass, selected);
          t.setAttribute('aria-selected', selected ? 'true' : 'false');
          t.setAttribute('tabindex', selected ? '0' : '-1');
        });

        if (currentLabel) {
          currentLabel.textContent = tab.textContent.trim();
        }

        syncTabList(tab);

        panels.forEach((p) => {
          const show = p.dataset.panel === tabId;
          p.classList.toggle(showClass, show);
        });

        const activePanel = panels.find((p) => p.dataset.panel === tabId);
        closeAccordionsIn(activePanel);
      };

      const initial =
        items.find((t) => t.classList.contains(currentClass)) || items[0];

      if (initial) activateTab(initial);

      listEl.addEventListener('click', (e) => {
        const tab = e.target.closest(item);
        if (!tab) return;
        activateTab(tab);

        const accordionItem = listEl.closest('[data-accordion-item]');

        if (accordionItem) {
          const heading = accordionItem.querySelector('[data-accordion-heading]');
          const panel = accordionItem.querySelector('[data-accordion-panel]');

          accordionItem.classList.remove('is-open');

          if (heading) {
            heading.classList.remove('is-open');
            heading.setAttribute('aria-expanded', 'false');
          }

          if (panel) {
            panel.classList.remove('is-open');
            panel.setAttribute('aria-hidden', 'true');
            panel.style.height = '0px';
          }
        }
      });

      listEl.addEventListener('keydown', (e) => {

        const currentIndex = items.findIndex((t) => t.getAttribute('aria-selected') === 'true');

        let nextIndex = null;

        if (e.key === 'ArrowRight') nextIndex = currentIndex + 1;
        if (e.key === 'ArrowLeft') nextIndex = currentIndex - 1;
        if (e.key === 'Home') nextIndex = 0;
        if (e.key === 'End') nextIndex = items.length - 1;

        if (nextIndex === null) return;

        e.preventDefault();

        if (nextIndex < 0) nextIndex = items.length - 1;
        if (nextIndex >= items.length) nextIndex = 0;

        items[nextIndex].focus();
        activateTab(items[nextIndex]);
      });

    });
  }

  initTabs();

  /* =========================================================
  Layer: 汎用（CSSはクラス、JSはdataのみ）
  ========================================================= */
  function initLayer(triggerContainer, layerRoot) {
    if (!(triggerContainer instanceof Element)) return;
    if (!(layerRoot instanceof Element)) return;

    const closeBtns = Array.from(layerRoot.querySelectorAll('[data-ui-close]'));

    const overlay =
      layerRoot.querySelector('[data-ui-close].c-modal__overlay') ||
      layerRoot.querySelector('[data-ui-close]');

    const ctrl = makeLayerController({
      root: layerRoot,
      overlay,
      openBtns: [],
      closeBtns,
      toggleBtns: [],
    });

    function openModal() {
      layerRoot.hidden = false;

      if (ctrl && typeof ctrl.open === 'function') {
        ctrl.open();
      }
    }

    triggerContainer.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-ui-trigger]');
      if (!trigger || !triggerContainer.contains(trigger)) return;

      openModal();
    });
  }

  function initLayersByScope(root = document) {
    const containers = Array.from(
      root.querySelectorAll('[data-ui-trigger-container][data-ui-scope]')
    );

    containers.forEach((container) => {
      const scope = container.getAttribute('data-ui-scope');
      if (!scope) return;

      const layerRoot = root.querySelector(
        `[data-ui-layer="${CSS.escape(scope)}"]`
      );

      if (!layerRoot) return;

      initLayer(container, layerRoot);
    });
  }

  initLayersByScope();

  /* =========================================================
  slider
  ========================================================= */
  const topSlider = new SlickSlider('.top-slide', {
    variableWidth: false,
    fade: true,
    autoplaySpeed: 1000,
    speed: 3000,
  })

  const pickupSliderEl = document.querySelector('.top-pickup__article-wrap');

  if (pickupSliderEl && pickupSliderEl.children.length > 3) {
    const topPickupSlider = new SlickSlider('.top-pickup__article-wrap', {
      autoplay: false,
      arrows: true,
      centerMode: false,
      slidesToShow: 3,
      appendArrows: '.slide-arrow-box',
      prevArrow: '<div class="slide-arrow prev-arrow"></div>',
      nextArrow: '<div class="slide-arrow next-arrow"></div>',
      responsive: [
        {
          breakpoint: 1500,
          settings: {
            centerMode: true,
          }
        }
      ],
    });
  }

  const historySlider = new SlickSlider('.co-history-slide', {
    centerMode: false,
    speed: 3000,
    cssEase: 'linear',
  })

  document.querySelectorAll('.chintai__slides').forEach((slider) => {
    const wrap = slider.closest('.chintai__slides-wrap');

    let arrowBox = null;

    if (wrap) {
      arrowBox = wrap.querySelector('.chintai__slides-arrow-box');
    }

    if (arrowBox) {
      new SlickSlider(slider, {
        autoplay: false,
        centerMode: false,
        arrows: true,
        slidesToShow: 3,
        appendArrows: arrowBox,
        prevArrow: '<div class="slide-rental-arrow prev-rental-arrow"></div>',
        nextArrow: '<div class="slide-rental-arrow next-rental-arrow"></div>',
        responsive: [
          {
            breakpoint: 960,
            settings: {
              slidesToShow: 1,
            }
          }
        ]
      });
    }
  });

  const recruitSlider = new SlickSlider('.recruit-top-fv__slides', {
    centerMode: false,
    speed: 4000,
    cssEase: 'linear',
  })

  const recruitPickupSlider = new SlickSlider('.recruit-top-pickup__article-wrap', {
    autoplay: false,
    arrows: true,
    centerMode: false,
    slidesToShow: 3,
    appendArrows: '.slide-arrow-box',
    prevArrow: '<div class="slide-arrow prev-arrow m--recruit"></div>',
    nextArrow: '<div class="slide-arrow next-arrow m--recruit"></div>',
    responsive: [
      {
        breakpoint: 960,
        settings: {
          slidesToShow: 1,
          centerMode: true,
        }
      }
    ]
  })

  /* =========================================================
  ニュース切り替え
  ========================================================= */
  function initTopNewsSlider() {
    const root = document.querySelector('.js-top-news');
    if (!root) return;

    const items = root.querySelectorAll('.js-top-news-item');
    if (items.length <= 1) {
      if (items.length === 1) {
        items[0].classList.add('is-active');
      }
      return;
    }

    let currentIndex = 0;
    const interval = 4000;

    items[currentIndex].classList.add('is-active');

    setInterval(() => {
      items[currentIndex].classList.remove('is-active');
      currentIndex = (currentIndex + 1) % items.length;
      items[currentIndex].classList.add('is-active');
    }, interval);
  }
  initTopNewsSlider();

  /* =========================================================
  沿革アニメーション
  ========================================================= */
  function initStoryPhaseAnimation() {
    const phases = document.querySelectorAll('.story-phase');

    if (!phases.length) {
      return;
    }

    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) {
          return;
        }

        entry.target.classList.add('is-animated');
        obs.unobserve(entry.target);
      });
    }, {
      root: null,
      rootMargin: '0px 0px -10% 0px',
      threshold: 0,
    });

    phases.forEach(phase => {
      observer.observe(phase);
    });
  }
  initStoryPhaseAnimation();
  /* =========================================================
  仕事紹介追従メニュー
  ========================================================= */
  function initFadeToggleOnCenter(options) {
    const {
      targetSelector,
      toggleTargetSelector,
      activeClass = 'is-visible',
    } = options;

    const target = document.querySelector(targetSelector);
    const toggleTarget = document.querySelector(toggleTargetSelector);

    if (!target || !toggleTarget) {
      return;
    }

    function toggle() {
      const rect = target.getBoundingClientRect();
      const viewportCenter = window.innerHeight / 2;

      toggleTarget.classList.toggle(activeClass, rect.top <= viewportCenter);
    }

    toggle();
    window.addEventListener('scroll', toggle, { passive: true });
    window.addEventListener('resize', toggle);
  }

  initFadeToggleOnCenter({
    targetSelector: '.job-desc-common.m--first',
    toggleTargetSelector: '.job-desc-menu',
  });
  initFadeToggleOnCenter({
    targetSelector: '.job-desc-common.m--first',
    toggleTargetSelector: '.desc-sp-trigger',
  });
  initFadeToggleOnCenter({
    targetSelector: '.env-sticky-wrap',
    toggleTargetSelector: '.env-fixed-menu',
  });
  /* =========================================================
  CF7
  ========================================================= */
  function initJobTitleSync() {
    const jobSelect = document.querySelector('#job_id');
    const jobTitleInput = document.querySelector('#job_title');

    if (!jobSelect || !jobTitleInput) {
      return;
    }

    const setJobTitle = () => {
      const selectedOption = jobSelect.options[jobSelect.selectedIndex];
      jobTitleInput.value = selectedOption ? selectedOption.textContent.trim() : '';
    };

    setJobTitle();
    jobSelect.addEventListener('change', setJobTitle);
  }
  function initDatePicker() {
    const inputs = document.querySelectorAll('.js-datepicker');

    if (!inputs.length) {
      return;
    }

    inputs.forEach((input) => {
      if (input.classList.contains('flatpickr-input')) {
        return;
      }

      flatpickr(input, {
        locale: 'ja',
        dateFormat: 'Y年m月d日',
        // maxDate: 'today',
        disableMobile: true,
      });
    });
  }

  initJobTitleSync();
  initDatePicker();
  document.addEventListener('wpcf7init', initDatePicker);

  function initCustomSelect() {
    const customSelects = document.querySelectorAll('.js-custom-select');

    customSelects.forEach((customSelect, selectIndex) => {

      const select = customSelect.querySelector('.js-native-select');

      const hiddenTitle = customSelect.querySelector(
        '.js-select-hidden'
      );

      const button = customSelect.querySelector(
        '.js-select-button'
      );

      const list = customSelect.querySelector(
        '.js-select-list'
      );

      if (!select || !button || !list) {
        return;
      }

      select.style.display = 'none';

      list.id = `custom-select-list-${selectIndex}`;

      list.setAttribute('role', 'listbox');

      button.setAttribute('aria-controls', list.id);

      const options = Array.from(select.options);

      let activeIndex =
        select.selectedIndex >= 0
          ? select.selectedIndex
          : 0;

      let selectedIndex = activeIndex;

      function openList() {

        button.classList.add('is-open');

        list.classList.add('is-open');

        button.setAttribute(
          'aria-expanded',
          'true'
        );

        updateActive(activeIndex);
      }

      function closeList() {

        button.classList.remove('is-open');

        list.classList.remove('is-open');

        button.setAttribute(
          'aria-expanded',
          'false'
        );
      }

      function updateActive(index) {

        const items = list.querySelectorAll(
          '.custom-select__option'
        );

        items.forEach((item) => {
          item.classList.remove('is-active');
        });

        if (!items[index]) {
          return;
        }

        activeIndex = index;

        items[index].classList.add('is-active');

        button.setAttribute(
          'aria-activedescendant',
          items[index].id
        );

        items[index].scrollIntoView({
          block: 'nearest',
        });
      }

      function selectOption(index) {

        const option = options[index];

        const items = list.querySelectorAll(
          '.custom-select__option'
        );

        if (!option) {
          return;
        }

        selectedIndex = index;

        activeIndex = index;

        select.value = option.value;

        button.textContent =
          option.textContent.trim();

        if (hiddenTitle) {
          hiddenTitle.value =
            option.textContent.trim();
        }

        items.forEach((item) => {

          item.classList.remove('is-selected');

          item.setAttribute(
            'aria-selected',
            'false'
          );
        });

        if (items[index]) {

          items[index].classList.add(
            'is-selected'
          );

          items[index].setAttribute(
            'aria-selected',
            'true'
          );
        }

        select.dispatchEvent(
          new Event('change', {
            bubbles: true,
          })
        );

        closeList();

        button.focus();
      }

      list.innerHTML = '';

      options.forEach((option, index) => {

        const item = document.createElement('li');

        item.id =
          `custom-select-option-${selectIndex}-${index}`;

        item.className =
          'custom-select__option';

        item.textContent =
          option.textContent;

        item.dataset.value =
          option.value;

        item.setAttribute(
          'role',
          'option'
        );

        item.setAttribute(
          'aria-selected',
          index === selectedIndex
            ? 'true'
            : 'false'
        );

        if (index === selectedIndex) {

          item.classList.add(
            'is-selected'
          );
        }

        item.addEventListener(
          'click',
          () => {
            selectOption(index);
          }
        );

        item.addEventListener(
          'mouseenter',
          () => {
            updateActive(index);
          }
        );

        list.appendChild(item);
      });

      const selectedOption =
        options[selectedIndex];

      if (
        selectedOption &&
        selectedOption.value !== ''
      ) {

        button.textContent =
          selectedOption.textContent.trim();

        if (hiddenTitle) {

          hiddenTitle.value =
            selectedOption.textContent.trim();
        }

      } else {

        button.textContent =
          '選択してください';
      }

      button.addEventListener(
        'click',
        () => {

          if (
            list.classList.contains(
              'is-open'
            )
          ) {

            closeList();

          } else {

            openList();
          }
        }
      );

      button.addEventListener(
        'keydown',
        (event) => {

          const isOpen =
            list.classList.contains(
              'is-open'
            );

          if (
            event.key === 'ArrowDown'
          ) {

            event.preventDefault();

            if (!isOpen) {

              openList();

              return;
            }

            const nextIndex =
              activeIndex + 1 >=
                options.length
                ? 0
                : activeIndex + 1;

            updateActive(nextIndex);
          }

          if (
            event.key === 'ArrowUp'
          ) {

            event.preventDefault();

            if (!isOpen) {

              openList();

              return;
            }

            const prevIndex =
              activeIndex - 1 < 0
                ? options.length - 1
                : activeIndex - 1;

            updateActive(prevIndex);
          }

          if (
            event.key === 'Enter' ||
            event.key === ' '
          ) {

            event.preventDefault();

            if (!isOpen) {

              openList();

              return;
            }

            selectOption(activeIndex);
          }

          if (
            event.key === 'Escape'
          ) {

            event.preventDefault();

            closeList();
          }

          if (
            event.key === 'Tab'
          ) {

            closeList();
          }
        }
      );
    });

    document.addEventListener(
      'click',
      (event) => {

        if (
          event.target.closest(
            '.custom-select'
          )
        ) {
          return;
        }

        document
          .querySelectorAll(
            '.custom-select__button'
          )
          .forEach((button) => {

            button.classList.remove(
              'is-open'
            );

            button.setAttribute(
              'aria-expanded',
              'false'
            );
          });

        document
          .querySelectorAll(
            '.custom-select__list'
          )
          .forEach((list) => {

            list.classList.remove(
              'is-open'
            );
          });
      }
    );
  }

  initCustomSelect();
});
